/**
 * WaitlistPage.jsx
 * UniLedge Founding Member Waitlist — 8-section homepage conversion
 *
 * Place at: src/pages/WaitlistPage.jsx
 *
 * Sections:
 *   1. Hero — headline + live counter + CTA
 *   2. Why Now — macro savings context (evergreen)
 *   3. How It Works — 3-step visual
 *   4. Founding 50 Preview — curated product carousel
 *   5. Tiered Signup Flow — progressive profiling + token meter
 *   6. Referral Mechanic — share link + tracker
 *   7. AVA Chatbot — already global, just needs to be unhidden on this route
 *   8. Footer — mission + links
 *
 * Dependencies: React, react-router-dom, AuthContext (Firebase Google Auth),
 *               waitlistService.js, Tailwind CSS (project already uses it).
 *
 * Fonts: Bricolage Grotesque (headlines) + DM Sans (body) — loaded via <link> in index.html
 *        See WAITLIST_INTEGRATION_GUIDE.md for the exact link tags.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as waitlistService from '../services/waitlistService';

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

const FOUNDING_50_PREVIEW = [
  { name: 'Mahatma Rice (5 lb)',        category: 'Pantry Staples',  retailPrice: 6.49,  savingsPercent: 28, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994587/mahatma-rice_bolydb.png' },
  { name: 'Goya Pinto Beans (4 lb)',    category: 'Pantry Staples',  retailPrice: 4.99,  savingsPercent: 32, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994586/goya-pinto-beans_qqi3ay.png' },
  { name: 'Barilla Pasta (16 oz)',      category: 'Pantry Staples',  retailPrice: 2.29,  savingsPercent: 26, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994607/barilla-pasta_yujnia.png' },
  { name: 'Jif Peanut Butter (28 oz)', category: 'Pantry Staples',  retailPrice: 5.99,  savingsPercent: 24, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994607/jif-peanut-butter_bpwv2w.png' },
  { name: 'Hunt\'s Diced Tomatoes (28 oz)', category: 'Pantry Staples', retailPrice: 2.49, savingsPercent: 30, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994651/hunts-diced-tomatoes_easgyn.png' },
  { name: 'Cheerios Cereal (18 oz)',    category: 'Pantry Staples',  retailPrice: 5.79,  savingsPercent: 27, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994650/cheerios-cereal_itzjoc.png' },
  { name: 'Bertolli Olive Oil (17 oz)', category: 'Pantry Staples',  retailPrice: 7.99,  savingsPercent: 22, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994652/bertolli-olive-oil_nxlnvp.png' },
  { name: 'Quaker Oats (42 oz)',        category: 'Pantry Staples',  retailPrice: 5.49,  savingsPercent: 34, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994652/quaker-oats_jto8ra.png' },
  { name: 'Tide Pods (42 ct)',          category: 'Household',       retailPrice: 13.99, savingsPercent: 35, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994653/tide-pods_vdiwpw.png' },
  { name: 'Fabuloso Cleaner (56 oz)',   category: 'Household',       retailPrice: 4.49,  savingsPercent: 31, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994651/fabuloso-cleaner_wtyhkf.png' },
  { name: 'Dawn Dish Soap (22 oz)',     category: 'Household',       retailPrice: 3.99,  savingsPercent: 29, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994703/dawn-dish-soap_tbt93i.png' },
  { name: 'Charmin Ultra Soft (12-roll)', category: 'Household',     retailPrice: 12.99, savingsPercent: 27, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994706/charmin-ultra-soft_cy88uh.png' },
  { name: 'Hefty Trash Bags (45 ct)',   category: 'Household',       retailPrice: 8.99,  savingsPercent: 33, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994707/hefty-trash-bags_oldddx.png' },
  { name: 'Huggies Diapers (size 3, 25 ct)', category: 'Baby & Family', retailPrice: 12.49, savingsPercent: 30, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994706/huggies-diapers_fkagtk.png' },
  { name: 'Huggies Baby Wipes (64 ct)',category: 'Baby & Family',   retailPrice: 3.99,  savingsPercent: 28, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994706/huggies-baby-wipes_ehcgbw.png' },
  { name: 'Dove Body Wash (24 oz)',     category: 'Personal Care',   retailPrice: 6.99,  savingsPercent: 28, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994703/dove-body-wash_sgroqg.png' },
  { name: 'Folgers Coffee (12 oz)',     category: 'Beverages',       retailPrice: 8.99,  savingsPercent: 30, image: 'https://res.cloudinary.com/drcldizc0/image/upload/v1774994704/folgers-coffee_yzzgcv.png' },
];

const CATEGORIES = ['All', 'Pantry Staples', 'Household', 'Baby & Family', 'Personal Care', 'Beverages'];

const TOKEN_TIERS = [
  { key: 'quick',   label: 'Quick Signup',     tokens: 50,  total: 50,  icon: '⚡' },
  { key: 'phone',   label: 'Add Phone Number',  tokens: 25,  total: 75,  icon: '📱' },
  { key: 'profile', label: 'Complete Profile',  tokens: 50,  total: 125, icon: '👤' },
];

const MAX_TOKENS = 125;

// ═══════════════════════════════════════════════════════════════════════
// EMBEDDED STYLES (custom animations Tailwind can't handle)
// ═══════════════════════════════════════════════════════════════════════

const EMBEDDED_CSS = `
  @keyframes wl-fade-up {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes wl-counter-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.04); }
  }
  @keyframes wl-meter-fill {
    from { width: 0%; }
  }
  @keyframes wl-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes wl-float {
    0%, 100% { transform: translateY(0px); }
    50%      { transform: translateY(-6px); }
  }
  @keyframes wl-check-pop {
    0%   { transform: scale(0); opacity: 0; }
    60%  { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
  }

  .wl-fade-up       { animation: wl-fade-up 0.6s ease-out both; }
  .wl-fade-up-d1    { animation: wl-fade-up 0.6s ease-out 0.1s both; }
  .wl-fade-up-d2    { animation: wl-fade-up 0.6s ease-out 0.2s both; }
  .wl-fade-up-d3    { animation: wl-fade-up 0.6s ease-out 0.3s both; }
  .wl-fade-up-d4    { animation: wl-fade-up 0.6s ease-out 0.4s both; }
  .wl-counter-pulse { animation: wl-counter-pulse 2s ease-in-out infinite; }
  .wl-meter-fill    { animation: wl-meter-fill 0.8s ease-out both; }
  .wl-float         { animation: wl-float 3s ease-in-out infinite; }
  .wl-check-pop     { animation: wl-check-pop 0.4s ease-out both; }

  .wl-shimmer {
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%);
    background-size: 200% 100%;
    animation: wl-shimmer 2s infinite;
  }

  /* Waitlist page root variables */
  .wl-page {
    --wl-green:       #16803C;
    --wl-green-dark:  #14532D;
    --wl-green-light: #DCFCE7;
    --wl-amber:       #D97706;
    --wl-amber-light: #FEF3C7;
    --wl-cream:       #FFFBF5;
    --wl-text:        #1C1917;
    --wl-text-muted:  #78716C;
    --wl-card:        #FFFFFF;
    --wl-border:      #E7E5E4;
    font-family: 'DM Sans', system-ui, sans-serif;
    color: var(--wl-text);
  }

  .wl-page h1, .wl-page h2, .wl-page h3 {
    font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  }

  /* Scroll reveal — elements start invisible, JS adds .wl-visible */
  .wl-reveal {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  }
  .wl-reveal.wl-visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* Product carousel horizontal scroll (mobile) */
  .wl-product-scroll {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;
    scrollbar-width: none;
  }
  .wl-product-scroll::-webkit-scrollbar { display: none; }
  .wl-product-scroll > * { scroll-snap-align: start; flex-shrink: 0; }

  /* CTA button glow */
  .wl-cta-glow {
    box-shadow: 0 0 0 0 rgba(22, 128, 60, 0.4);
    transition: box-shadow 0.3s ease;
  }
  .wl-cta-glow:hover {
    box-shadow: 0 0 20px 4px rgba(22, 128, 60, 0.25);
  }

  /* Token meter stripe pattern for unfilled area */
  .wl-meter-track {
    background: repeating-linear-gradient(
      -45deg,
      #f5f5f4,
      #f5f5f4 4px,
      #e7e5e4 4px,
      #e7e5e4 8px
    );
  }
`;

// ═══════════════════════════════════════════════════════════════════════
// UTILITY HOOKS
// ═══════════════════════════════════════════════════════════════════════

/** Intersection observer for scroll-triggered reveals */
function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('wl-visible'); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/** Animated number counter */
function useAnimatedCounter(target, duration = 1800) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || startedRef.current || !target) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const animate = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
          setValue(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.unobserve(el);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { ref, value };
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════

// ──── Animated Counter Display ──────────────────────────────────────
function CounterDisplay({ count }) {
  const { ref, value } = useAnimatedCounter(count);
  return (
    <span ref={ref} className="wl-counter-pulse inline-block tabular-nums font-bold" style={{ fontFamily: "'Bricolage Grotesque', system-ui" }}>
      {value.toLocaleString()}
    </span>
  );
}

// ──── Token Meter ───────────────────────────────────────────────────
function TokenMeter({ earned, showLabels = true }) {
  const pct = Math.min((earned / MAX_TOKENS) * 100, 100);
  return (
    <div className="w-full">
      {showLabels && (
        <div className="flex justify-between items-baseline mb-2">
          <span className="text-sm font-semibold" style={{ color: 'var(--wl-green-dark)' }}>
            {earned} / {MAX_TOKENS} tokens earned
          </span>
          <span className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>
            = ${(earned * 0.10).toFixed(2)} in savings
          </span>
        </div>
      )}
      <div className="wl-meter-track h-4 rounded-full overflow-hidden relative">
        <div
          className="wl-meter-fill h-full rounded-full relative"
          style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #16803C 0%, #22c55e 100%)' }}
        >
          <div className="wl-shimmer absolute inset-0 rounded-full" />
        </div>
      </div>
      {showLabels && (
        <div className="flex justify-between mt-2">
          {TOKEN_TIERS.map((t) => {
            const pos = (t.total / MAX_TOKENS) * 100;
            const done = earned >= t.total;
            return (
              <div key={t.key} className="flex flex-col items-center text-center" style={{ width: `${100/3}%` }}>
                <span className={`text-xs font-medium ${done ? 'text-green-700' : 'text-stone-400'}`}>
                  {done ? '✓ ' : ''}{t.icon} +{t.tokens}
                </span>
                <span className="text-[10px] text-stone-400 mt-0.5 leading-tight">{t.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──── Product Card ──────────────────────────────────────────────────
// Category color map for professional product cards
const CATEGORY_COLORS = {
  'Pantry Staples': { bg: '#FEF3C7', accent: '#D97706', text: '#92400E' },
  'Household':      { bg: '#DBEAFE', accent: '#2563EB', text: '#1E3A8A' },
  'Personal Care':  { bg: '#FCE7F3', accent: '#DB2777', text: '#831843' },
  'Beverages':      { bg: '#D1FAE5', accent: '#059669', text: '#064E3B' },
};

function ProductCard({ product }) {
  const colors = CATEGORY_COLORS[product.category] || CATEGORY_COLORS['Pantry Staples'];

  return (
    <div
      className="rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col"
      style={{
        background: 'var(--wl-card)',
        borderColor: 'var(--wl-border)',
      }}
    >
      {/* Product image area */}
      <div className="relative" style={{ background: colors.bg, minHeight: '128px' }}>
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            style={{ width: '100%', height: '128px', objectFit: 'contain', padding: '12px', display: 'block' }}
            onError={(e) => { console.error('[ProductCard] Image failed:', product.name, product.image); e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-32 flex items-center justify-center">
            <span className="text-3xl font-bold opacity-20" style={{ color: colors.accent }}>
              {product.name.charAt(0)}
            </span>
          </div>
        )}
        {/* Savings badge — compact, top-right */}
        <span
          className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-md"
          style={{ background: 'var(--wl-green)', color: '#fff' }}
        >
          Save up to {product.savingsPercent}%
        </span>
      </div>

      {/* Product info */}
      <div className="px-3 py-3 flex-1 flex flex-col">
        <span
          className="inline-block self-start text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full mb-1.5"
          style={{ background: colors.bg, color: colors.text }}
        >
          {product.category}
        </span>
        <h4 className="text-xs font-semibold leading-snug mb-2 flex-1" style={{ color: 'var(--wl-text)' }}>
          {product.name}
        </h4>
        <span className="text-[11px] line-through" style={{ color: 'var(--wl-text-muted)' }}>
          ${product.retailPrice.toFixed(2)} retail
        </span>
        <p className="text-[9px] mt-1" style={{ color: 'var(--wl-text-muted)' }}>
          Founding Member pricing coming soon
        </p>
      </div>
    </div>
  );
}

// ──── Step Icon (How It Works) ──────────────────────────────────────
function StepIcon({ number, emoji, label, description, delay }) {
  const r = useScrollReveal();
  return (
    <div ref={r} className="wl-reveal flex flex-col items-center text-center px-4" style={{ transitionDelay: `${delay}s` }}>
      <div
        className="wl-float w-20 h-20 rounded-2xl flex items-center justify-center text-3xl mb-4 shadow-md"
        style={{ background: 'var(--wl-green-light)', animationDelay: `${delay}s` }}
      >
        {emoji}
      </div>
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mb-3"
        style={{ background: 'var(--wl-green)', color: '#fff' }}
      >
        {number}
      </div>
      <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--wl-green-dark)' }}>{label}</h3>
      <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'var(--wl-text-muted)' }}>{description}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 1 — HERO
// ═══════════════════════════════════════════════════════════════════════

function HeroSection({ stats, isInSacramento, detectedCity, onCTAClick, hasSignedUp, signupData }) {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #14532D 0%, #16803C 40%, #22c55e 100%)',
      }}
    >
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
        backgroundSize: '32px 32px',
      }} />

      <div className="relative z-10 max-w-2xl mx-auto text-center">
        {/* Badge */}
        <div className="wl-fade-up inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8">
          <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
          <span className="text-white/90 text-sm font-medium">
            {isInSacramento ? 'You\'re in the launch zone' : 'Launching in Sacramento first'}
          </span>
        </div>

        {/* Headline */}
        <h1 className="wl-fade-up-d1 text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.1] mb-6 tracking-tight">
          Sacramento, stop overpaying for groceries.
        </h1>

        {/* Subhead */}
        <p className="wl-fade-up-d2 text-lg sm:text-xl text-white/80 mb-8 max-w-lg mx-auto leading-relaxed">
          Join{' '}
          <CounterDisplay count={stats?.totalSignups || 0} />{' '}
          Sacramento families saving up to <span className="text-amber-300 font-bold">40%</span> through
          community buying power.
        </p>

        {/* Geo message */}
        <p className="wl-fade-up-d2 text-sm text-white/60 mb-6">
          {isInSacramento
            ? 'Founding Members in Sacramento get early access and bonus rewards.'
            : detectedCity
              ? `We're launching in Sacramento first. Be the first to know when we expand to ${detectedCity}.`
              : 'Sign up to lock in your Founding Member spot.'}
        </p>

        {/* CTA */}
        {!hasSignedUp ? (
          <button
            onClick={onCTAClick}
            className="wl-fade-up-d3 wl-cta-glow inline-flex items-center gap-2 bg-white text-green-800 font-bold text-lg px-8 py-4 rounded-full hover:bg-green-50 transition-all duration-300 active:scale-95"
          >
            Become a Founding Member
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
              <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <div className="wl-fade-up-d3 flex flex-col items-center gap-2">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-3">
              <span className="wl-check-pop text-2xl">✓</span>
              <span className="text-white font-semibold">You're on the list!</span>
            </div>
            {signupData?.position && (
              <p className="text-white/70 text-sm">
                {isInSacramento
                  ? `You're in our launch zone! Founding Members here get early access.`
                  : detectedCity
                    ? `You're #${signupData.position} on the waitlist for ${detectedCity}. Share to help bring UniLedge to your area sooner!`
                    : `You're #${signupData.position} on the waitlist.`}
              </p>
            )}
          </div>
        )}

        {/* Social proof nudge */}
        {stats?.recentSignups > 0 && (
          <p className="wl-fade-up-d4 text-white/50 text-xs mt-6">
            {stats.recentSignups} people signed up in the last 24 hours
          </p>
        )}
      </div>

      {/* Bottom wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-16 sm:h-20">
          <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="#FFFBF5" />
        </svg>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 2 — WHY NOW
// ═══════════════════════════════════════════════════════════════════════

// ──── Stat Card (extracted so useScrollReveal is called at component level) ──
function StatCard({ stat, index }) {
  const cardRef = useScrollReveal();
  return (
    <div
      ref={cardRef}
      className="wl-reveal rounded-2xl p-6 sm:p-8 border text-center"
      style={{
        background: 'var(--wl-card)',
        borderColor: 'var(--wl-border)',
        transitionDelay: `${index * 0.12}s`,
      }}
    >
      <span className="text-4xl block mb-3">{stat.icon}</span>
      <p className="text-3xl sm:text-4xl font-extrabold mb-2" style={{ color: 'var(--wl-green)' }}>{stat.figure}</p>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--wl-text-muted)' }}>{stat.label}</p>
    </div>
  );
}

function WhyNowSection() {
  const r = useScrollReveal();
  const stats = [
    {
      figure: '38%',
      label: 'Grocery prices have risen over 38% in the past 5 years.',
      icon: '📈',
    },
    {
      figure: '$400+',
      label: 'The average Sacramento family overspends per month compared to bulk buyers.',
      icon: '💸',
    },
    {
      figure: '25-40%',
      label: 'What you save when your neighborhood buys together.',
      icon: '🏘️',
    },
  ];

  return (
    <section className="py-20 sm:py-28 px-4" style={{ background: 'var(--wl-cream)' }}>
      <div ref={r} className="wl-reveal max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: 'var(--wl-green-dark)' }}>
          Grocery prices keep climbing. Your paycheck isn't.
        </h2>
        <p className="text-base sm:text-lg mb-4 max-w-xl mx-auto" style={{ color: 'var(--wl-text-muted)' }}>
          Every dollar matters more right now.
          Families are stretched thinner than ever — and the grocery store isn't helping.
        </p>
        <p className="text-base sm:text-lg font-semibold mb-14 max-w-xl mx-auto" style={{ color: 'var(--wl-green)' }}>
          UniLedge is building something different.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {stats.map((s, i) => (
            <StatCard key={i} stat={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 3 — HOW IT WORKS
// ═══════════════════════════════════════════════════════════════════════

function HowItWorksSection() {
  const r = useScrollReveal();
  const r2 = useScrollReveal();
  const r3 = useScrollReveal();

  return (
    <section className="py-20 sm:py-28 px-4" style={{ background: '#fff' }}>
      <div className="max-w-4xl mx-auto">
        <div ref={r} className="wl-reveal text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: 'var(--wl-green-dark)' }}>
            How You Actually Save Money
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: 'var(--wl-text-muted)' }}>
            Stores charge retail markup because you're buying alone.
            UniLedge removes that markup by combining your order with your neighbors' — automatically.
          </p>
        </div>

        {/* The Mechanism — explained clearly */}
        <div className="space-y-6 mb-14">
          {/* Step 1 */}
          <div ref={r2} className="wl-reveal flex gap-4 sm:gap-6 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                 style={{ background: 'var(--wl-green)' }}>1</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--wl-green-dark)' }}>
                We buy at wholesale — the same prices stores pay
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--wl-text-muted)' }}>
                UniLedge sources products directly from wholesale distributors — the same suppliers that stock grocery store shelves.
                A 5 lb bag of rice that costs you <strong>$6.49 at the store</strong> costs the store about <strong>$3.50 from the distributor</strong>.
                That $3 gap? That's the retail markup you've been paying. We cut it out.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4 sm:gap-6 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                 style={{ background: 'var(--wl-green)' }}>2</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--wl-green-dark)' }}>
                Your neighborhood orders together — without even knowing it
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--wl-text-muted)' }}>
                When you order rice, and your neighbor three streets over orders rice, and a family across the zip code orders rice — UniLedge
                automatically combines those into one bulk purchase. You don't coordinate anything. You just shop.
                The system groups orders from your area using your zip code so everyone gets better pricing.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4 sm:gap-6 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                 style={{ background: 'var(--wl-green)' }}>3</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--wl-green-dark)' }}>
                More neighbors = deeper discount. Automatically.
              </h3>
              <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--wl-text-muted)' }}>
                Distributors give better prices for larger orders. As more people in your area buy the same product,
                the per-unit cost drops — and those savings go directly to you:
              </p>

              {/* Pricing tier table */}
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--wl-border)' }}>
                <div className="grid grid-cols-3 text-xs font-semibold uppercase tracking-wider py-2 px-3"
                     style={{ background: 'var(--wl-green-light)', color: 'var(--wl-green-dark)' }}>
                  <span>Neighbors Buying</span>
                  <span className="text-center">Your Discount</span>
                  <span className="text-right">On a $50 Order</span>
                </div>
                {[
                  { neighbors: '5+',  discount: '6–11%',  saved: '$3 – $5.50' },
                  { neighbors: '10+', discount: '11–18%', saved: '$5.50 – $9' },
                  { neighbors: '25+', discount: '17–26%', saved: '$8.50 – $13' },
                  { neighbors: '50+', discount: '20–35%', saved: '$10 – $17.50' },
                ].map((tier, i) => (
                  <div key={i}
                       className={`grid grid-cols-3 text-sm py-2.5 px-3 ${i % 2 === 0 ? 'bg-white' : ''}`}
                       style={i % 2 !== 0 ? { background: 'var(--wl-cream)' } : {}}>
                    <span className="font-medium" style={{ color: 'var(--wl-text)' }}>{tier.neighbors}</span>
                    <span className="text-center font-semibold" style={{ color: 'var(--wl-green)' }}>{tier.discount}</span>
                    <span className="text-right font-semibold" style={{ color: 'var(--wl-amber)' }}>Save {tier.saved}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex gap-4 sm:gap-6 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                 style={{ background: 'var(--wl-green)' }}>4</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--wl-green-dark)' }}>
                Pick up Saturday at the farmer's market — or choose your own window.
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--wl-text-muted)' }}>
                Primary pickup is at a <strong>local Saturday farmer's market meetup point</strong> — a real community gathering, not a warehouse loading dock.
                Need delivery? Community members in your area earn tokens by delivering to neighbors.
                You can also choose your own pickup or delivery window at checkout.
              </p>
              <p className="text-sm leading-relaxed mt-2" style={{ color: 'var(--wl-text-muted)' }}>
                Here's the key: <strong>your payment is held securely in escrow</strong> until the buying window closes.
                The longer the window stays open, the more neighbors can join, and the deeper your discount goes.
                If 10 people are in your group when you order but 25 are in by the time the window closes,
                <strong style={{ color: 'var(--wl-green)' }}> UniLedge automatically refunds the price difference back to your original payment method.</strong>
                No membership fees, no delivery surcharges, no minimum order.
              </p>
            </div>
          </div>

          {/* Cross-zone note */}
          <div className="flex gap-4 sm:gap-6 items-start">
            <div className="flex-shrink-0 w-12 h-12" />
            <div className="flex-1 rounded-xl p-4 border" style={{ background: 'var(--wl-cream)', borderColor: 'var(--wl-border)' }}>
              <p className="text-sm" style={{ color: 'var(--wl-text-muted)' }}>
                <strong style={{ color: 'var(--wl-green-dark)' }}>What if not enough people near me sign up?</strong>{' '}
                The system automatically pools buyers from adjacent neighborhoods when local density is low.
                You still save — the group just covers a wider area with a slightly longer fulfillment window.
              </p>
            </div>
          </div>
        </div>

        {/* Worked example — makes it real */}
        <div ref={r3} className="wl-reveal rounded-2xl p-6 sm:p-8 border" style={{ background: 'var(--wl-green-light)', borderColor: '#bbf7d0' }}>
          <h3 className="text-base font-bold mb-4" style={{ color: 'var(--wl-green-dark)' }}>
            Real Example: A Family's Weekly Essentials
          </h3>
          <div className="space-y-2 text-sm mb-4" style={{ color: 'var(--wl-text)' }}>
            {[
              { item: 'Mahatma Rice (5 lb)',    store: '$6.49',  uni: '$4.67',  save: '$1.82' },
              { item: 'Tide Pods (42 ct)',       store: '$13.99', uni: '$9.09',  save: '$4.90' },
              { item: 'Jif Peanut Butter (28 oz)', store: '$5.99', uni: '$4.55', save: '$1.44' },
              { item: 'Charmin Ultra (12-roll)', store: '$12.99', uni: '$9.48',  save: '$3.51' },
              { item: 'Folgers Coffee (12 oz)',  store: '$8.99',  uni: '$6.29',  save: '$2.70' },
            ].map((row, i) => (
              <div key={i} className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: '#bbf7d0' }}>
                <span className="flex-1">{row.item}</span>
                <span className="w-16 text-center line-through opacity-60">{row.store}</span>
                <span className="w-16 text-center font-semibold" style={{ color: 'var(--wl-green-dark)' }}>{row.uni}</span>
                <span className="w-20 text-right font-bold" style={{ color: 'var(--wl-green)' }}>-{row.save}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center pt-2">
            <span className="text-sm font-medium" style={{ color: 'var(--wl-text-muted)' }}>
              Just 5 items, one week
            </span>
            <span className="text-lg font-extrabold" style={{ color: 'var(--wl-green-dark)' }}>
              You save $14.37
            </span>
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--wl-text-muted)' }}>
            Example assumes 25+ neighbors buying. Actual prices set at launch with verified wholesale sourcing. Add tokens on top for even deeper savings.
          </p>
        </div>

        {/* Costco/Sam's Club differentiation — Item 27 */}
        <div className="mt-8 rounded-2xl border p-6 text-center" style={{ background: 'var(--wl-card)', borderColor: 'var(--wl-border)' }}>
          <h3 className="text-base font-bold mb-2" style={{ color: 'var(--wl-green-dark)' }}>
            "How is this different from Costco or Sam's Club?"
          </h3>
          <p className="text-sm max-w-lg mx-auto" style={{ color: 'var(--wl-text-muted)' }}>
            No membership fee. No warehouse trip. No buying 48 rolls of toilet paper at once.
            UniLedge gives you <strong>wholesale-level prices on normal quantities</strong> — because your neighborhood is buying together, even if you never coordinate a thing.
          </p>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 4 — FOUNDING 50 PREVIEW
// ═══════════════════════════════════════════════════════════════════════

function Founding50Section() {
  const r = useScrollReveal();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const filtered = selectedCategory === 'All'
    ? FOUNDING_50_PREVIEW
    : FOUNDING_50_PREVIEW.filter(p => p.category === selectedCategory);

  return (
    <section className="py-20 sm:py-28 px-4" style={{ background: 'var(--wl-cream)' }}>
      <div ref={r} className="wl-reveal max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: 'var(--wl-green-dark)' }}>
            The Founding 50
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: 'var(--wl-text-muted)' }}>
            Your first catalog — everyday essentials at wholesale prices.
            Founding Member pricing locked in at launch.
          </p>
        </div>

        {/* Category filter */}
        <div className="flex gap-2 justify-center flex-wrap mb-8">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setSelectedCategory(c)}
              className={`text-sm font-medium px-4 py-2 rounded-full border transition-all duration-200 ${
                selectedCategory === c
                  ? 'border-green-700 text-white'
                  : 'border-stone-200 hover:border-stone-300'
              }`}
              style={selectedCategory === c
                ? { background: 'var(--wl-green)', borderColor: 'var(--wl-green)' }
                : { background: 'var(--wl-card)', color: 'var(--wl-text-muted)' }
              }
            >
              {c}
            </button>
          ))}
        </div>

        {/* Product grid — single responsive layout */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {filtered.map((p) => <ProductCard key={p.name} product={p} />)}
        </div>

        <p className="text-center text-xs mt-8" style={{ color: 'var(--wl-text-muted)' }}>
          Actual savings vary by product and group size. Prices finalized at launch with verified wholesale sourcing.
        </p>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 4.5 — TOKEN EXPLAINER (What are tokens? Why should I care?)
// ═══════════════════════════════════════════════════════════════════════

function TokenExplainerSection() {
  const r = useScrollReveal();
  const r2 = useScrollReveal();

  return (
    <section className="py-20 sm:py-28 px-4" style={{ background: '#fff' }}>
      <div className="max-w-3xl mx-auto">
        <div ref={r} className="wl-reveal text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: 'var(--wl-green-dark)' }}>
            Tokens: Savings on Top of Savings
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: 'var(--wl-text-muted)' }}>
            Group buying already cuts your prices. Tokens take it further — real money off your order, earned just by being part of the community.
          </p>
        </div>

        {/* What tokens are */}
        <div ref={r2} className="wl-reveal space-y-6">
          {/* Core explainer */}
          <div className="rounded-2xl border p-6" style={{ background: 'var(--wl-cream)', borderColor: 'var(--wl-border)' }}>
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--wl-green-dark)' }}>What are tokens?</h3>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--wl-text)' }}>
              Tokens are UniLedge's reward currency. <strong>10 tokens = $1.00.</strong> They work like store credit — 
              you earn them for things you'd do anyway (signing up, referring friends, completing your profile), 
              and you spend them at checkout for real dollars off your order.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--wl-text)' }}>
              Tokens <strong>stack on top of group discounts</strong>. So if your neighborhood already unlocked 26% off through 
              group buying, your tokens take the price down even further. It's not one or the other — it's both.
            </p>
          </div>

          {/* How you earn — visual grid */}
          <div>
            <h3 className="text-base font-bold mb-4" style={{ color: 'var(--wl-green-dark)' }}>How you earn tokens</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  title: 'Sign up as a Founding Member',
                  detail: 'Up to 125 tokens ($12.50) across three signup tiers — before we even launch.',
                  amount: '125',
                },
                {
                  title: 'Refer friends & family',
                  detail: '25 tokens ($2.50) for every person who joins through your link. No cap.',
                  amount: '25/each',
                },
                {
                  title: 'Make purchases',
                  detail: 'Earn tokens on every order. Choose pickup over delivery for bonus tokens.',
                  amount: '~10/item',
                },
                {
                  title: 'Help your community',
                  detail: 'Sort orders at pickup, coordinate with neighbors, deliver nearby — earn tokens that deepen your discount on every future order.',
                  amount: '15–50',
                },
              ].map((item, i) => (
                <div key={i} className="rounded-xl border p-4 flex gap-3" style={{ background: 'var(--wl-card)', borderColor: 'var(--wl-border)' }}>
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center text-sm font-extrabold"
                       style={{ background: 'var(--wl-green-light)', color: 'var(--wl-green-dark)' }}>
                    {item.amount}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-0.5" style={{ color: 'var(--wl-text)' }}>{item.title}</h4>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--wl-text-muted)' }}>{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dual value prop — Save AND Earn (Item 10, 11) */}
          <div className="rounded-2xl border p-6" style={{ background: 'var(--wl-cream)', borderColor: 'var(--wl-border)' }}>
            <h3 className="text-base font-bold mb-3" style={{ color: 'var(--wl-green-dark)' }}>Save money. Earn for your community.</h3>
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--wl-text)' }}>
              UniLedge isn't just cheaper groceries. When you help sort orders at a Saturday pickup, coordinate with your neighbors, 
              or deliver to someone down the street, you <strong>earn tokens that stack on top of your group discounts</strong>.
              The more you participate, the higher your loyalty tier, and the more tokens you can redeem per order.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--wl-text)' }}>
              This is how UniLedge keeps money circulating in YOUR community — not flowing out to a corporate warehouse 
              in another state. Every dollar you save and every token you earn stays local.
            </p>
          </div>

          {/* How you spend */}
          <div className="rounded-2xl border p-6" style={{ background: 'var(--wl-green-light)', borderColor: '#bbf7d0' }}>
            <h3 className="text-base font-bold mb-3" style={{ color: 'var(--wl-green-dark)' }}>How you spend tokens</h3>
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--wl-text)' }}>
              At checkout, you apply tokens for dollars off — up to <strong>10% of your cart as a new member (capped at $15)</strong>. 
              As you shop more and refer more people, your loyalty tier increases and your cap rises:
            </p>
            <div className="grid grid-cols-5 text-center text-xs gap-1">
              {[
                { tier: 'New',      pct: '10%', cap: '$15',  color: '#78716C' },
                { tier: 'Bronze',   pct: '15%', cap: '$25',  color: '#B45309' },
                { tier: 'Silver',   pct: '20%', cap: '$40',  color: '#6B7280' },
                { tier: 'Gold',     pct: '30%', cap: '$75',  color: '#D97706' },
                { tier: 'Platinum', pct: '40%', cap: 'No cap', color: '#7C3AED' },
              ].map((t, i) => (
                <div key={i} className="rounded-lg py-2 px-1" style={{ background: '#fff' }}>
                  <span className="block font-bold text-[11px]" style={{ color: t.color }}>{t.tier}</span>
                  <span className="block font-extrabold text-base" style={{ color: 'var(--wl-green)' }}>{t.pct}</span>
                  <span className="block text-[10px]" style={{ color: 'var(--wl-text-muted)' }}>up to {t.cap}</span>
                </div>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: 'var(--wl-text-muted)' }}>
              The system always applies whichever limit is lower — your percentage cap or your dollar cap.
              Platinum members (50+ purchases, 10+ referrals) get uncapped 40% redemption and tokens that never expire.
            </p>
          </div>

          {/* The bottom line */}
          <div className="text-center p-5 rounded-xl" style={{ background: 'var(--wl-amber-light)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--wl-amber)' }}>
              Bottom line: Sign up now, earn up to 125 tokens before launch, then use them on top of your group discount on day one.
              That's up to $12.50 off your first order — earned just by participating.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 5 — TIERED SIGNUP FLOW
// ═══════════════════════════════════════════════════════════════════════

function SignupFlowSection({
  signupStep, signupData, tokensEarned, isLoading, error,
  onGoogleSignup, onEmailSignup, onPhoneSubmit, onProfileSubmit,
  detectedZip, detectedCity, isInSacramento,
}) {
  const [emailInput, setEmailInput] = useState('');
  const [zipInput, setZipInput] = useState(detectedZip || '');
  const [phoneInput, setPhoneInput] = useState('');
  const [profileForm, setProfileForm] = useState({ name: '', address: '', householdSize: '', budget: '', preferences: '' });
  const sectionRef = useRef(null);

  // Pre-fill zip when geo-detection completes
  useEffect(() => {
    if (detectedZip && !zipInput) setZipInput(detectedZip);
  }, [detectedZip]);

  // Expose ref so parent can scroll to it
  useEffect(() => {
    if (sectionRef.current) sectionRef.current.id = 'signup-section';
  }, []);

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 px-4" style={{ background: '#fff' }}>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: 'var(--wl-green-dark)' }}>
            Become a Founding Member
          </h2>
          <p className="text-base" style={{ color: 'var(--wl-text-muted)' }}>
            Complete each step to earn tokens — real savings on your first order.
          </p>
        </div>

        {/* Token Meter */}
        <div className="mb-10">
          <TokenMeter earned={tokensEarned} />
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 rounded-xl text-sm border border-red-200 bg-red-50 text-red-700">
            {error}
          </div>
        )}

        {/* ── STEP 1: Quick Signup ───────────────────────────── */}
        <div
          className="rounded-2xl border p-6 mb-4 transition-all duration-300"
          style={{
            borderColor: signupStep === 'initial' ? 'var(--wl-green)' : 'var(--wl-border)',
            background: signupStep === 'initial' ? 'var(--wl-green-light)' : signupData ? '#f0fdf4' : 'var(--wl-card)',
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            {signupData ? (
              <span className="wl-check-pop w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">✓</span>
            ) : (
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--wl-green)', color: '#fff' }}>1</span>
            )}
            <div>
              <h3 className="font-bold text-base" style={{ color: 'var(--wl-green-dark)' }}>Quick Signup</h3>
              <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>Earn 50 tokens ($5.00 in savings)</p>
            </div>
          </div>

          {signupStep === 'initial' && !signupData && (
            <div className="space-y-3">
              {/* Google Auth (primary) */}
              <button
                onClick={onGoogleSignup}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 bg-white border border-stone-200 rounded-xl px-4 py-3 font-medium text-sm hover:bg-stone-50 transition-colors disabled:opacity-50"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {isLoading ? 'Signing up…' : 'Sign up with Google'}
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-stone-400">or use email</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>

              {/* Email fallback */}
              <div className="space-y-2">
                {detectedCity && (
                  <p className="text-xs px-1" style={{ color: 'var(--wl-green-dark)' }}>
                    We detected you're near <strong>{detectedCity}</strong>. Enter your home zip code for accurate results and earn extra tokens.
                  </p>
                )}
                <input
                  type="email"
                  placeholder="you@email.com"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
                />
                <input
                  type="text"
                  placeholder={detectedZip ? `Zip code (detected: ${detectedZip})` : 'Home zip code'}
                  value={zipInput}
                  onChange={e => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
                  maxLength={5}
                />
                <button
                  onClick={() => onEmailSignup(emailInput, zipInput)}
                  disabled={isLoading || !emailInput || zipInput.length < 5}
                  className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors disabled:opacity-40"
                  style={{ background: 'var(--wl-green)' }}
                >
                  {isLoading ? 'Signing up…' : 'Join the Waitlist'}
                </button>
              </div>
            </div>
          )}

          {signupData && (
            <div className="space-y-2">
              <p className="text-sm text-green-700 font-medium">
                Signed up as {signupData.email} — 50 tokens earned!
              </p>
              {signupData.position && (
                <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>
                  {isInSacramento
                    ? 'You\'re in our launch zone! Founding Members in Sacramento get early access.'
                    : detectedCity
                      ? `You're #${signupData.position} on the waitlist for ${detectedCity}. Share your referral link to help bring UniLedge to your area sooner!`
                      : `You're #${signupData.position} on the waitlist.`}
                </p>
              )}
              <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>
                We'll send you newsletters and launch updates by email. Add your phone number below and we'll text you when UniLedge opens in your area.
              </p>
            </div>
          )}
        </div>

        {/* Privacy callout — Item 9 */}
        {signupStep === 'initial' && !signupData && (
          <div className="mb-4 px-2">
            <p className="text-[11px] text-center leading-relaxed" style={{ color: 'var(--wl-text-muted)' }}>
              We never sell your data. Your location is only used for neighborhood grouping — other members cannot see who's in their zone. 
              By signing up you agree to our <a href="/privacy" className="underline">Privacy Policy</a> and <a href="/terms" className="underline">Terms</a>.
            </p>
          </div>
        )}

        {/* ── STEP 2: Add Phone ──────────────────────────────── */}
        <div
          className="rounded-2xl border p-6 mb-4 transition-all duration-300"
          style={{
            borderColor: signupStep === 'phone' ? 'var(--wl-green)' : 'var(--wl-border)',
            background: signupStep === 'phone' ? 'var(--wl-green-light)' : tokensEarned >= 75 ? '#f0fdf4' : 'var(--wl-card)',
            opacity: signupData ? 1 : 0.5,
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            {tokensEarned >= 75 ? (
              <span className="wl-check-pop w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">✓</span>
            ) : (
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: signupData ? 'var(--wl-green)' : '#a8a29e', color: '#fff' }}>2</span>
            )}
            <div>
              <h3 className="font-bold text-base" style={{ color: 'var(--wl-green-dark)' }}>Add Phone Number</h3>
              <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>Earn 25 more tokens ($2.50) — for order updates & pickup alerts</p>
            </div>
          </div>

          {signupStep === 'phone' && (
            <div className="flex gap-2">
              <input
                type="tel"
                placeholder="(916) 555-0123"
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
                className="flex-1 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
              />
              <button
                onClick={() => onPhoneSubmit(phoneInput)}
                disabled={isLoading || phoneInput.length < 10}
                className="rounded-xl px-5 py-3 text-sm font-semibold text-white transition-colors disabled:opacity-40"
                style={{ background: 'var(--wl-green)' }}
              >
                {isLoading ? '…' : 'Add'}
              </button>
            </div>
          )}

          {tokensEarned >= 75 && signupStep !== 'phone' && (
            <p className="text-sm text-green-700 font-medium">Phone added — 25 tokens earned!</p>
          )}
        </div>

        {/* ── STEP 3: Complete Profile ───────────────────────── */}
        <div
          className="rounded-2xl border p-6 mb-4 transition-all duration-300"
          style={{
            borderColor: signupStep === 'profile' ? 'var(--wl-green)' : 'var(--wl-border)',
            background: signupStep === 'profile' ? 'var(--wl-green-light)' : tokensEarned >= 125 ? '#f0fdf4' : 'var(--wl-card)',
            opacity: signupData ? 1 : 0.5,
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            {tokensEarned >= 125 ? (
              <span className="wl-check-pop w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">✓</span>
            ) : (
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: signupData ? 'var(--wl-green)' : '#a8a29e', color: '#fff' }}>3</span>
            )}
            <div>
              <h3 className="font-bold text-base" style={{ color: 'var(--wl-green-dark)' }}>Complete Your Profile</h3>
              <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>Earn 50 more tokens ($5.00) — helps us stock what you actually need</p>
            </div>
          </div>

          {signupStep === 'profile' && (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Your name"
                value={profileForm.name}
                onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
              />
              <input
                type="text"
                placeholder="Home address (for delivery zone)"
                value={profileForm.address}
                onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
              />
              <select
                value={profileForm.householdSize}
                onChange={e => setProfileForm(p => ({ ...p, householdSize: e.target.value }))}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 bg-white"
              >
                <option value="">Household size</option>
                <option value="1">1 person</option>
                <option value="2">2 people</option>
                <option value="3-4">3–4 people</option>
                <option value="5+">5+ people</option>
              </select>
              <select
                value={profileForm.budget}
                onChange={e => setProfileForm(p => ({ ...p, budget: e.target.value }))}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 bg-white"
              >
                <option value="">Weekly grocery budget</option>
                <option value="under-50">Under $50/week</option>
                <option value="50-100">$50 – $100/week</option>
                <option value="100-150">$100 – $150/week</option>
                <option value="150-200">$150 – $200/week</option>
                <option value="200+">$200+/week</option>
              </select>
              <select
                value={profileForm.preferences}
                onChange={e => setProfileForm(p => ({ ...p, preferences: e.target.value }))}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 bg-white"
              >
                <option value="">What matters most to you?</option>
                <option value="lowest_price">Lowest prices possible</option>
                <option value="organic_natural">Organic & natural products</option>
                <option value="variety">Wide variety of options</option>
                <option value="convenience">Convenient pickup/delivery</option>
              </select>
              <button
                onClick={() => onProfileSubmit(profileForm)}
                disabled={isLoading || !profileForm.name || !profileForm.householdSize}
                className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors disabled:opacity-40"
                style={{ background: 'var(--wl-green)' }}
              >
                {isLoading ? 'Saving…' : 'Complete Profile (+50 tokens)'}
              </button>
            </div>
          )}

          {tokensEarned >= 125 && signupStep !== 'profile' && (
            <p className="text-sm text-green-700 font-medium">Profile complete — all 125 tokens earned!</p>
          )}
        </div>

        {/* Skip links between steps */}
        {signupStep === 'phone' && (
          <button
            onClick={() => onPhoneSubmit(null)} // skip
            className="block mx-auto text-xs underline mt-2 mb-4"
            style={{ color: 'var(--wl-text-muted)' }}
          >
            Skip for now — I'll add it later
          </button>
        )}
        {signupStep === 'profile' && (
          <button
            onClick={() => onProfileSubmit(null)} // skip
            className="block mx-auto text-xs underline mt-2 mb-4"
            style={{ color: 'var(--wl-text-muted)' }}
          >
            Skip for now
          </button>
        )}

        {/* Value reminder */}
        <div className="text-center mt-6 p-4 rounded-xl" style={{ background: 'var(--wl-amber-light)' }}>
          <p className="text-sm font-medium" style={{ color: 'var(--wl-amber)' }}>
            💡 Your tokens = real savings. 125 tokens = $12.50 off your first order.
          </p>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 6 — REFERRAL MECHANIC
// ═══════════════════════════════════════════════════════════════════════

function ReferralSection({ signupData, referralCode, referralStats, isInSacramento, detectedCity }) {
  const [copied, setCopied] = useState(false);
  const r = useScrollReveal();

  if (!signupData) return null; // Only show after signup

  const referralLink = `${window.location.origin}?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join UniLedge',
          text: 'Join UniLedge and save up to 40% on groceries through community buying power. We both earn bonus tokens!',
          url: referralLink,
        });
      } catch {} // user cancelled share
    } else {
      handleCopy();
    }
  };

  return (
    <section className="py-20 sm:py-28 px-4" style={{ background: 'var(--wl-cream)' }}>
      <div ref={r} className="wl-reveal max-w-xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: 'var(--wl-green-dark)' }}>
          Spread the Word, Earn More
        </h2>
        <p className="text-base mb-4" style={{ color: 'var(--wl-text-muted)' }}>
          Every friend who joins earns you <strong>25 tokens ($2.50)</strong>. No limit.
        </p>
        {/* Item 12: Area-launch urgency */}
        <p className="text-sm font-medium mb-8" style={{ color: 'var(--wl-green)' }}>
          {isInSacramento
            ? 'The more Sacramento neighbors who sign up, the sooner we launch. Your referrals accelerate the timeline.'
            : detectedCity
              ? `The more people from ${detectedCity} who sign up, the sooner we launch there. You're not just joining a waitlist — you're voting for your community.`
              : 'The more people who sign up from your area, the sooner we launch there. Share your link!'}
        </p>

        {/* Referral link display */}
        <div className="rounded-2xl border p-6 text-left" style={{ background: 'var(--wl-card)', borderColor: 'var(--wl-border)' }}>
          <label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: 'var(--wl-text-muted)' }}>
            Your referral link
          </label>
          <div className="flex gap-2 mb-4">
            <input
              readOnly
              value={referralLink}
              className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm font-mono truncate"
            />
            <button
              onClick={handleCopy}
              className="rounded-xl px-4 py-3 text-sm font-semibold border transition-all duration-200"
              style={copied
                ? { background: 'var(--wl-green-light)', borderColor: 'var(--wl-green)', color: 'var(--wl-green-dark)' }
                : { borderColor: 'var(--wl-border)', color: 'var(--wl-text)' }
              }
            >
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>

          <button
            onClick={handleShare}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors"
            style={{ background: 'var(--wl-green)' }}
          >
            Share with Friends
          </button>

          {/* Referral stats */}
          {referralStats && (referralStats.totalReferrals > 0 || referralStats.totalEarnings > 0) && (
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t" style={{ borderColor: 'var(--wl-border)' }}>
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: 'var(--wl-green)' }}>{referralStats.totalReferrals}</p>
                <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>Friends joined</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: 'var(--wl-green)' }}>{referralStats.totalEarnings}</p>
                <p className="text-xs" style={{ color: 'var(--wl-text-muted)' }}>Tokens earned</p>
              </div>
            </div>
          )}
        </div>

        {/* Exclusivity + demand framing — Items 21, 22 */}
        <div className="mt-6 space-y-3">
          <div className="p-4 rounded-xl text-left" style={{ background: 'var(--wl-green-light)' }}>
            <p className="text-sm mb-2" style={{ color: 'var(--wl-green-dark)' }}>
              <strong>This is not a first-come-first-served waitlist.</strong> Founding Members are curated based on 
              zip code density, referral activity, profile completion, and willingness to be community ambassadors.
              The more you engage, the sooner you'll be invited.
            </p>
            <p className="text-sm" style={{ color: 'var(--wl-green-dark)' }}>
              We're building something worth waiting for — and <strong>your demand determines our launch timeline.</strong> Every 
              signup and every referral accelerates when UniLedge opens in your area.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECTION 8 — FOOTER
// ═══════════════════════════════════════════════════════════════════════

function FooterSection() {
  const socials = [
    { name: 'TikTok',    url: 'https://tiktok.com/@joinuniledge',    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.71a8.27 8.27 0 004.76 1.5v-3.4a4.85 4.85 0 01-1-.12z"/></svg>
    )},
    { name: 'Instagram', url: 'https://instagram.com/joinuniledge',  icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
    )},
    { name: 'YouTube',   url: 'https://youtube.com/@joinuniledge',   icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
    )},
    { name: 'Facebook',  url: 'https://facebook.com/joinuniledge',   icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    )},
    { name: 'X',         url: 'https://x.com/joinuniledge',         icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    )},
    { name: 'Snapchat',  url: 'https://snapchat.com/add/joinuniledge', icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.922-.214.04-.012.06-.012.1 0 .16.06.32.18.32.42 0 .18-.12.3-.24.36-.12.048-.54.192-1.14.33-.18.03-.24.06-.27.09-.06.12-.03.36.12.66l.015.027c.654 1.14 1.575 2.073 2.745 2.763.18.105.42.225.66.27.12.03.18.15.18.24 0 .27-.36.48-.66.57-.3.09-.81.18-1.05.24-.06.03-.12.09-.18.21l-.03.06c-.12.27-.18.54-.36.72-.18.18-.48.27-.78.27-.3 0-.6-.06-.84-.18-.36-.12-.78-.27-1.26-.27-.48 0-.84.12-1.14.27a5.6 5.6 0 01-.84.33c-.33.12-.66.12-.96 0-.3-.12-.54-.33-.72-.54l-.06-.06c-.12-.12-.18-.3-.18-.51-.06-.06-.06-.12-.12-.18-.24-.06-.72-.15-1.02-.24-.3-.09-.66-.3-.66-.57 0-.09.06-.21.18-.24.24-.045.48-.165.66-.27 1.17-.69 2.091-1.623 2.745-2.763l.015-.027c.15-.3.18-.54.12-.66-.03-.03-.09-.06-.27-.09-.54-.12-1.02-.282-1.14-.33-.12-.06-.24-.18-.24-.36 0-.24.16-.36.32-.42.04-.012.06-.012.1 0 .263.094.622.214.922.214.198 0 .326-.045.401-.09-.008-.165-.018-.33-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.853 1.069 11.216.793 12.206.793z"/></svg>
    )},
    { name: 'LinkedIn',  url: 'https://linkedin.com/company/joinuniledge', icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
    )},
  ];

  return (
    <footer className="py-12 px-4" style={{ background: 'var(--wl-green-dark)', color: 'rgba(255,255,255,0.7)' }}>
      <div className="max-w-4xl mx-auto text-center">
        <h3 className="text-xl font-bold text-white mb-2">UniLedge</h3>
        <p className="text-sm mb-6 max-w-md mx-auto leading-relaxed">
          A Sacramento-based company building economic infrastructure
          for communities that need it most.
        </p>

        {/* Social media links */}
        <div className="flex justify-center gap-4 mb-8">
          {socials.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Follow UniLedge on ${s.name}`}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            >
              {s.icon}
            </a>
          ))}
        </div>

        <p className="text-xs mb-6" style={{ color: 'rgba(255,255,255,0.5)' }}>
          @joinuniledge everywhere
        </p>

        {/* Legal links */}
        <div className="flex justify-center gap-6 text-sm mb-8">
          <a href="/transparency" className="hover:text-white transition-colors">Transparency</a>
          <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-white transition-colors">Terms</a>
          <a href="/cookies" className="hover:text-white transition-colors">Cookies</a>
          <a href="/help/support" className="hover:text-white transition-colors">Help</a>
        </div>

        <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
          Questions? Reach us at{' '}
          <a href="mailto:support@uniledge.io" className="underline hover:text-white transition-colors">support@uniledge.io</a>
        </p>

        <p className="text-xs opacity-60">
          &copy; {new Date().getFullYear()} UniLedge LLC. Sacramento, CA.
        </p>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function WaitlistPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInWithGoogle } = useAuth();

  // ── State ──────────────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [isInSacramento, setIsInSacramento] = useState(false);
  const [detectedCity, setDetectedCity] = useState('');
  const [detectedZip, setDetectedZip] = useState('');

  const [signupStep, setSignupStep] = useState('initial'); // initial | phone | profile | complete
  const [signupData, setSignupData] = useState(null);       // WaitlistSignup record from backend
  const [tokensEarned, setTokensEarned] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState(null);
  const [referredByCode, setReferredByCode] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Helper: extract numeric token value ────────────────────
  // Backend may return tokensEarned as a number OR as { breakdown: {...}, total: N }
  const parseTokens = (val, fallback = 0) => {
    if (typeof val === 'number') return val;
    if (val && typeof val === 'object' && typeof val.total === 'number') return val.total;
    return fallback;
  };

  // ── Source tracking ────────────────────────────────────────
  const getTrafficSource = useCallback(() => {
    const src = searchParams.get('utm_source') || searchParams.get('source');
    if (src) return src;
    const ref = searchParams.get('ref');
    if (ref) return 'referral';
    const referrer = document.referrer;
    if (referrer.includes('tiktok'))    return 'tiktok';
    if (referrer.includes('instagram')) return 'instagram';
    if (referrer.includes('youtube'))   return 'youtube';
    if (referrer.includes('facebook'))  return 'facebook';
    if (referrer.includes('twitter') || referrer.includes('x.com')) return 'x';
    if (referrer.includes('snapchat')) return 'snapchat';
    if (referrer.includes('linkedin')) return 'linkedin';
    return 'direct';
  }, [searchParams]);

  // ── Init: fetch stats, detect geo, capture referral ────────
  useEffect(() => {
    // 1. Waitlist stats
    waitlistService.getStats().then(res => {
      if (res.success) setStats(res.stats || res);
    });

    // 2. Geo detection
    waitlistService.detectLocation().then(res => {
      const loc = res.location || res;
      if (res.success || loc.city) {
        setDetectedCity(loc.city || '');
        const zip = loc.zip || loc.postalCode || '';
        setDetectedZip(zip);
        // Check Sacramento zone — by zip if available, by city name as fallback
        if (zip) {
          waitlistService.geoCheck(zip).then(geoRes => {
            if (geoRes.success !== false) setIsInSacramento(!!geoRes.isInLaunchZone);
          });
        } else if (loc.city && loc.city.toLowerCase().includes('sacramento')) {
          // IP detected Sacramento but no zip — treat as launch zone
          setIsInSacramento(true);
        }
      }
    });

    // 3. Referral code from URL
    const refCode = searchParams.get('ref') || searchParams.get('referral');
    if (refCode) {
      setReferredByCode(refCode);
      // Also save to sessionStorage (matches existing AuthContext pattern)
      sessionStorage.setItem('uniledge_referral_code', refCode);
      localStorage.setItem('uniledge_referral_code', refCode);
    }

    // 4. Check if user already signed up (from localStorage)
    const savedEmail = localStorage.getItem('wl_email');
    const savedSignupId = localStorage.getItem('wl_signup_id');
    if (savedEmail && savedSignupId) {
      waitlistService.getMySignup(savedEmail).then(res => {
        if (res.success !== false && res.signup) {
          restoreSignupState(res.signup);
        }
      });
    }
  }, [searchParams]);

  // ── Restore signup state from existing record ──────────────
  function restoreSignupState(signup) {
    // Normalize: backend may use _id, id, or signupId
    const normalized = { ...signup };
    if (!normalized._id && normalized.id) normalized._id = normalized.id;
    if (!normalized._id && normalized.signupId) normalized._id = normalized.signupId;
    
    console.log('[WaitlistPage] restoreSignupState:', JSON.stringify(normalized, null, 2));
    
    setSignupData(normalized);
    setReferralCode(normalized.referralCode || '');
    const earned = parseTokens(normalized.tokensEarned, 0);
    setTokensEarned(earned);

    if (earned >= 125 || normalized.signupTier === 'full') {
      setSignupStep('complete');
    } else if (earned >= 75 || normalized.signupTier === 'phone' || normalized.phone) {
      setSignupStep('profile');
    } else {
      setSignupStep('phone');
    }

    // Store email for future lookups
    if (normalized.email) localStorage.setItem('wl_email', normalized.email);
    if (normalized._id) localStorage.setItem('wl_signup_id', normalized._id);

    // Fetch referral stats
    if (normalized._id) {
      waitlistService.getReferralStats(normalized._id).then(res => {
        if (res.success !== false) setReferralStats(res);
      });
    }
  }

  // ── CTA click → scroll to signup ──────────────────────────
  const handleCTAClick = () => {
    const el = document.getElementById('signup-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Google Auth signup ────────────────────────────────────
  const handleGoogleSignup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const authResult = await signInWithGoogle();
      if (!authResult || !authResult.success) {
        setError('Google sign-in was cancelled or failed. Please try again.');
        setIsLoading(false);
        return;
      }

      const user = authResult.user;
      const email = user.email;
      const zip = detectedZip || '';

      // Check if already on waitlist
      const existing = await waitlistService.checkExisting(email);
      if (existing.exists) {
        const mySignup = await waitlistService.getMySignup(email);
        if (mySignup.signup) {
          restoreSignupState(mySignup.signup);
          setIsLoading(false);
          return;
        }
      }

      // New signup
      const res = await waitlistService.signup({
        email,
        zip,
        name: user.displayName || '',
        firebaseUid: user.uid,
        referredBy: referredByCode,
        source: getTrafficSource(),
      });

      if (res.success === false) {
        // 409 Conflict = email already exists — restore their signup state
        if (res.status === 409 || (res.error && res.error.toLowerCase().includes('already'))) {
          const mySignup = await waitlistService.getMySignup(email);
          console.log('[WaitlistPage] Google 409 — raw getMySignup:', JSON.stringify(mySignup));
          const signupObj = mySignup.signup || mySignup.data || mySignup;
          if (signupObj && (signupObj._id || signupObj.id || signupObj.email)) {
            restoreSignupState(signupObj);
            localStorage.setItem('wl_email', email);
            setIsLoading(false);
            return;
          }
        }
        setError(res.error || 'Signup failed. Please try again.');
      } else {
        const signup = res.signup || res;
        setSignupData(signup);
        setReferralCode(signup.referralCode || '');
        setTokensEarned(parseTokens(signup.tokensEarned, 50));
        setSignupStep('phone');
        localStorage.setItem('wl_email', email);
        localStorage.setItem('wl_signup_id', signup._id || '');
        // Refresh stats
        waitlistService.getStats().then(r => { if (r.success) setStats(r.stats || r); });
      }
    } catch (err) {
      console.error('[WaitlistPage] Google signup error:', err);
      setError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  // ── Email signup ──────────────────────────────────────────
  const handleEmailSignup = async (email, zip) => {
    if (!email || !zip || zip.length < 5) {
      setError('Please enter a valid email and 5-digit zip code.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      // Check existing
      const existing = await waitlistService.checkExisting(email);
      if (existing.exists) {
        const mySignup = await waitlistService.getMySignup(email);
        if (mySignup.signup) {
          restoreSignupState(mySignup.signup);
          setIsLoading(false);
          return;
        }
      }

      const res = await waitlistService.signup({
        email,
        zip,
        referredBy: referredByCode,
        source: getTrafficSource(),
      });

      if (res.success === false) {
        // 409 Conflict = email already exists — restore their signup state
        if (res.status === 409 || (res.error && res.error.toLowerCase().includes('already'))) {
          const mySignup = await waitlistService.getMySignup(email);
          console.log('[WaitlistPage] Email 409 — raw getMySignup:', JSON.stringify(mySignup));
          const signupObj = mySignup.signup || mySignup.data || mySignup;
          if (signupObj && (signupObj._id || signupObj.id || signupObj.email)) {
            restoreSignupState(signupObj);
            localStorage.setItem('wl_email', email);
            setIsLoading(false);
            return;
          }
        }
        setError(res.error || 'Signup failed. Please try again.');
      } else {
        const signup = res.signup || res;
        setSignupData(signup);
        setReferralCode(signup.referralCode || '');
        setTokensEarned(parseTokens(signup.tokensEarned, 50));
        setSignupStep('phone');
        localStorage.setItem('wl_email', email);
        localStorage.setItem('wl_signup_id', signup._id || '');
        // Geo check the entered zip
        waitlistService.geoCheck(zip).then(geoRes => {
          if (geoRes.success !== false) setIsInSacramento(!!geoRes.isInLaunchZone);
        });
        waitlistService.getStats().then(r => { if (r.success) setStats(r.stats || r); });
      }
    } catch (err) {
      console.error('[WaitlistPage] Email signup error:', err);
      setError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  // ── Phone upgrade ─────────────────────────────────────────
  const handlePhoneSubmit = async (phone) => {
    if (!phone) {
      // User skipped
      setSignupStep('profile');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const id = signupData._id || signupData.id || signupData.signupId;
      const email = signupData.email || localStorage.getItem('wl_email');
      console.log('[WaitlistPage] upgrade phone — id:', id, '| email:', email);
      const res = await waitlistService.upgradeTier(id, { phone, email });
      if (res.success === false) {
        setError(res.error || 'Failed to add phone. Please try again.');
      } else {
        const updated = res.signup || res;
        setSignupData(prev => ({ ...prev, ...updated }));
        setTokensEarned(parseTokens(updated.tokensEarned, tokensEarned + 25));
        setSignupStep('profile');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  // ── Profile upgrade ───────────────────────────────────────
  const handleProfileSubmit = async (profile) => {
    if (!profile) {
      // User skipped
      setSignupStep('complete');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const id = signupData._id || signupData.id || signupData.signupId;
      const email = signupData.email || localStorage.getItem('wl_email');
      console.log('[WaitlistPage] upgrade profile — id:', id, '| email:', email);
      const res = await waitlistService.upgradeTier(id, {
        email,
        name: profile.name,
        address: profile.address,
        householdSize: profile.householdSize,
        budget: profile.budget,
        preferences: profile.preferences,
      });
      if (res.success === false) {
        setError(res.error || 'Failed to update profile. Please try again.');
      } else {
        const updated = res.signup || res;
        setSignupData(prev => ({ ...prev, ...updated }));
        setTokensEarned(parseTokens(updated.tokensEarned, tokensEarned + 50));
        setSignupStep('complete');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="wl-page">
      <style>{EMBEDDED_CSS}</style>

      <HeroSection
        stats={stats}
        isInSacramento={isInSacramento}
        detectedCity={detectedCity}
        onCTAClick={handleCTAClick}
        hasSignedUp={!!signupData}
        signupData={signupData}
      />

      <WhyNowSection />

      <HowItWorksSection />

      <Founding50Section />

      <TokenExplainerSection />

      <SignupFlowSection
        signupStep={signupStep}
        signupData={signupData}
        tokensEarned={tokensEarned}
        isLoading={isLoading}
        error={error}
        onGoogleSignup={handleGoogleSignup}
        onEmailSignup={handleEmailSignup}
        onPhoneSubmit={handlePhoneSubmit}
        onProfileSubmit={handleProfileSubmit}
        detectedZip={detectedZip}
        detectedCity={detectedCity}
        isInSacramento={isInSacramento}
      />

      <ReferralSection
        signupData={signupData}
        referralCode={referralCode}
        referralStats={referralStats}
        isInSacramento={isInSacramento}
        detectedCity={detectedCity}
      />

      <FooterSection />
    </div>
  );
}